import React, { useState, useEffect, useCallback } from "react";
import { useMutation, gql } from "@apollo/client";
import { styled } from "@mui/material/styles";
import {
  Grid,
  Typography,
  Box,
  TextField,
  Switch,
  Alert,
  Paper,
  Autocomplete,
} from "@mui/material";
import {
  Schedule as ScheduleIcon,
  PhoneAndroid as MobileIcon,
  Timer as TimerIcon,
  Chat as ChatIcon,
  Public as PublicIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

// Styled Components - Matching ContactForm.js/TextColors.js pattern
const ConfigPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  height: "100%",
  backgroundColor: theme.palette.background.paper,
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const SettingsToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: 8,
  marginBottom: theme.spacing(1),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const ToggleInfo = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const ToggleLabel = styled(Typography)({
  fontWeight: 500,
  fontSize: 14,
});

const ToggleDescription = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
  marginTop: 2,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
  },
}));

// Preview Components
const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: "#f5f7fa",
  minHeight: 500,
  display: "flex",
  flexDirection: "column",
}));

const PreviewLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

const WebsitePreview = styled(Box)({
  backgroundColor: "#ffffff",
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  overflow: "hidden",
  position: "relative",
  minHeight: 350,
  border: "1px solid #e0e0e0",
  flex: 1,
});

const BrowserBar = styled(Box)({
  backgroundColor: "#f5f5f5",
  padding: "8px 12px",
  display: "flex",
  alignItems: "center",
  gap: 8,
  borderBottom: "1px solid #e0e0e0",
});

const BrowserDot = styled(Box)(({ color }) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  backgroundColor: color,
}));

const UrlBar = styled(Box)({
  flex: 1,
  backgroundColor: "#ffffff",
  borderRadius: 4,
  padding: "4px 12px",
  fontSize: 12,
  color: "#666",
  border: "1px solid #e0e0e0",
  marginLeft: 8,
});

const WebsiteContent = styled(Box)({
  padding: 20,
  height: "calc(100% - 50px)",
  display: "flex",
  flexDirection: "column",
  position: "relative",
});

const ContentPlaceholder = styled(Box)(({ width }) => ({
  height: 12,
  backgroundColor: "#e0e0e0",
  borderRadius: 4,
  marginBottom: 8,
  width: width || "100%",
}));

const FloatingButtonPreview = styled(Box)(({ buttoncolor, isvisible }) => ({
  position: "absolute",
  bottom: 20,
  right: 20,
  width: 56,
  height: 56,
  borderRadius: "50%",
  backgroundColor: buttoncolor || "#4CAF50",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
  color: "white",
  cursor: "pointer",
  transition: "all 0.3s ease",
  opacity: isvisible === "false" ? 0.3 : 1,
  transform: isvisible === "false" ? "scale(0.8)" : "scale(1)",
}));

const DelayIndicator = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 85,
  right: 20,
  backgroundColor: "rgba(0,0,0,0.7)",
  color: "white",
  padding: "4px 8px",
  borderRadius: 4,
  fontSize: 11,
  display: "flex",
  alignItems: "center",
  gap: 4,
}));

const StatusCard = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(255,255,255,0.9)",
  borderRadius: 8,
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const StatusRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(1, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderBottom: "none",
  },
}));

const StatusLabel = styled(Typography)({
  fontSize: 13,
  fontWeight: 500,
});

const StatusBadge = styled(Box)(({ status }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 12px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 500,
  backgroundColor: status === "active" ? "#e8f5e9" : "#ffebee",
  color: status === "active" ? "#2e7d32" : "#c62828",
}));

// Timezone options
const timezoneOptions = [
  { label: "UTC", value: "UTC" },
  { label: "America/New_York", value: "America/New_York" },
  { label: "America/Chicago", value: "America/Chicago" },
  { label: "America/Denver", value: "America/Denver" },
  { label: "America/Los_Angeles", value: "America/Los_Angeles" },
  { label: "Europe/London", value: "Europe/London" },
  { label: "Europe/Paris", value: "Europe/Paris" },
  { label: "Europe/Berlin", value: "Europe/Berlin" },
  { label: "Asia/Tokyo", value: "Asia/Tokyo" },
  { label: "Asia/Singapore", value: "Asia/Singapore" },
  { label: "Asia/Kolkata", value: "Asia/Kolkata" },
  { label: "Asia/Dubai", value: "Asia/Dubai" },
  { label: "Australia/Sydney", value: "Australia/Sydney" },
  { label: "Pacific/Auckland", value: "Pacific/Auckland" },
];

const UPDATE_WIDGET = gql`
  mutation UpdateWidget($id: ID!, $input: WidgetInput!) {
    updateWidget(id: $id, input: $input) {
      id
    }
  }
`;

function VisibilitySettings({
  handleChildData,
  widgetData,
  isEditing,
  widgetId,
  onSave,
}) {
  const [updateWidget] = useMutation(UPDATE_WIDGET);

  // State declarations for visibility settings
  const [alwaysOn, setAlwaysOn] = useState(true);
  const [customScheduleEnabled, setCustomScheduleEnabled] = useState(false);
  const [timezone, setTimezone] = useState("UTC");
  const [hideOnMobile, setHideOnMobile] = useState(false);
  const [hideWhenOffline, setHideWhenOffline] = useState(false);
  const [dialogDelaySeconds, setDialogDelaySeconds] = useState(4);
  const [schedule, setSchedule] = useState({
    sunday: { enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
    monday: { enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
    tuesday: { enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
    wednesday: { enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
    thursday: { enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
    friday: { enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
    saturday: { enabled: true, slots: [{ start: "09:00", end: "17:00" }] },
  });

  // Get widget colors for preview
  const buttonColor = widgetData?.welcome_screen?.button_color || "#4CAF50";
  const fontColor = widgetData?.welcome_screen?.font_color || "#ffffff";

  // Initialize state from widgetData when editing
  useEffect(() => {
    if (widgetData?.visibility_settings) {
      setAlwaysOn(widgetData.visibility_settings.always_on ?? true);
      setCustomScheduleEnabled(
        widgetData.visibility_settings.custom_schedule_enabled ?? false
      );
      setTimezone(widgetData.visibility_settings.timezone || "UTC");
      setHideOnMobile(widgetData.visibility_settings.hide_on_mobile ?? false);
      setHideWhenOffline(
        widgetData.visibility_settings.hide_when_offline ?? false
      );
      setDialogDelaySeconds(
        widgetData.visibility_settings.dialog_delay_seconds ?? 4
      );
      if (widgetData.visibility_settings.schedule) {
        setSchedule(widgetData.visibility_settings.schedule);
      }
    }
  }, [widgetData]);

  const handleSaveVisibilitySettings = useCallback(async () => {
    if (!widgetId) {
      console.error("No widgetId available");
      return;
    }

    try {
      const widgetInput = {
        business_details: widgetData?.business_details
          ? {
              business_type: widgetData.business_details.business_type,
              name: widgetData.business_details.name,
              display_name: widgetData.business_details.display_name,
              website_url: widgetData.business_details.website_url,
            }
          : undefined,
        website_integration: widgetData?.website_integration
          ? {
              mode: widgetData.website_integration.mode,
              button_style: widgetData.website_integration.button_style,
              position: widgetData.website_integration.position,
              square_buttom: widgetData.website_integration.square_buttom,
              minimize_small_button:
                widgetData.website_integration.minimize_small_button,
              preview: widgetData.website_integration.preview,
              media: widgetData.website_integration.media,
              picture: widgetData.website_integration.picture,
              speeach_bubble_test:
                widgetData.website_integration.speeach_bubble_test,
              call_to_action_button_text:
                widgetData.website_integration.call_to_action_button_text,
              slogan: widgetData.website_integration.slogan,
            }
          : undefined,
        welcome_screen: widgetData?.welcome_screen
          ? {
              font_shadow: widgetData.welcome_screen.font_shadow,
              desc: widgetData.welcome_screen.desc,
              customizable_button:
                widgetData.welcome_screen.customizable_button,
              button_color: widgetData.welcome_screen.button_color,
              font_color: widgetData.welcome_screen.font_color,
              icon_color: widgetData.welcome_screen.icon_color,
              welcome_message: widgetData.welcome_screen.welcome_message,
            }
          : undefined,
        contact_options: widgetData?.contact_options
          ? {
              chat_enabled: widgetData.contact_options.chat_enabled,
              phone_enabled: widgetData.contact_options.phone_enabled,
              video_enabled: widgetData.contact_options.video_enabled,
              audio_enabled: widgetData.contact_options.audio_enabled,
            }
          : undefined,
        request_routing: widgetData?.request_routing
          ? {
              preselection_enabled:
                widgetData.request_routing.preselection_enabled,
              automatic_response_enabled:
                widgetData.request_routing.automatic_response_enabled,
              automatic_response_text:
                widgetData.request_routing.automatic_response_text,
              lead_form_enabled: widgetData.request_routing.lead_form_enabled,
              privacy_enabled: widgetData.request_routing.privacy_enabled,
              privacy_text: widgetData.request_routing.privacy_text,
            }
          : undefined,
        session_end: widgetData?.session_end
          ? {
              title: widgetData.session_end.title,
              closing_message: widgetData.session_end.closing_message,
              background_color: widgetData.session_end.background_color,
              text_color: widgetData.session_end.text_color,
              show_feedback_form: widgetData.session_end.show_feedback_form,
              rating_type: widgetData.session_end.rating_type,
            }
          : undefined,
        contact_form: widgetData?.contact_form
          ? {
              unavailable_message: widgetData.contact_form.unavailable_message,
              background_color: widgetData.contact_form.background_color,
              text_color: widgetData.contact_form.text_color,
              form_fields: widgetData.contact_form.form_fields?.map(
                ({ __typename, ...field }) => field
              ),
              privacy_enabled: widgetData.contact_form.privacy_enabled,
              privacy_text: widgetData.contact_form.privacy_text,
            }
          : undefined,
        visibility_settings: {
          always_on: alwaysOn,
          custom_schedule_enabled: customScheduleEnabled,
          timezone: timezone,
          schedule: schedule,
          hide_on_mobile: hideOnMobile,
          hide_when_offline: hideWhenOffline,
          dialog_delay_seconds: parseInt(dialogDelaySeconds, 10) || 4,
        },
      };

      const { data } = await updateWidget({
        variables: { id: widgetId, input: widgetInput },
      });

      console.log("Visibility settings saved successfully");
      return { widgetId, widgetData: data.updateWidget };
    } catch (err) {
      console.error("Error saving visibility settings:", err);
      throw err;
    }
  }, [
    widgetId,
    widgetData,
    alwaysOn,
    customScheduleEnabled,
    timezone,
    schedule,
    hideOnMobile,
    hideWhenOffline,
    dialogDelaySeconds,
    updateWidget,
  ]);

  useEffect(() => {
    if (onSave && typeof onSave === "function") {
      onSave(handleSaveVisibilitySettings);
    }
  }, [onSave, handleSaveVisibilitySettings]);

  // Determine if widget is visible based on current settings
  const isWidgetVisible = alwaysOn && !hideOnMobile && !hideWhenOffline;

  return (
    <React.Fragment>
      <Grid justifyContent="space-between" container>
        <Grid item md={12}>
          <Typography variant="h4" gutterBottom>
            Configure Visibility Settings
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Control when and how your chat widget appears on your website.
            Configure availability, device visibility, and display timing.
          </Alert>

          <Grid container spacing={3}>
            {/* Configuration Panel */}
            <Grid item xs={12} md={5}>
              <ConfigPanel elevation={2}>
                {/* Section 1: Availability Mode */}
                <Section>
                  <SectionTitle variant="subtitle1">
                    <ScheduleIcon color="primary" />
                    Availability Mode
                  </SectionTitle>

                  <SettingsToggle>
                    <ToggleInfo>
                      <ToggleLabel>Always ON</ToggleLabel>
                      <ToggleDescription>
                        Widget is always visible to visitors
                      </ToggleDescription>
                    </ToggleInfo>
                    <Switch
                      checked={alwaysOn}
                      onChange={(e) => setAlwaysOn(e.target.checked)}
                      color="primary"
                    />
                  </SettingsToggle>

                  <SettingsToggle>
                    <ToggleInfo>
                      <ToggleLabel>Custom Schedule</ToggleLabel>
                      <ToggleDescription>
                        Show widget only during business hours
                      </ToggleDescription>
                    </ToggleInfo>
                    <Switch
                      checked={customScheduleEnabled}
                      onChange={(e) =>
                        setCustomScheduleEnabled(e.target.checked)
                      }
                      color="primary"
                    />
                  </SettingsToggle>

                  {customScheduleEnabled && (
                    <Box sx={{ mt: 2, ml: 2 }}>
                      <Autocomplete
                        disablePortal
                        id="timezone-select"
                        options={timezoneOptions}
                        value={
                          timezoneOptions.find((t) => t.value === timezone) || {
                            label: timezone,
                            value: timezone,
                          }
                        }
                        onChange={(e, newValue) =>
                          setTimezone(newValue?.value || "UTC")
                        }
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => (
                          <StyledTextField
                            {...params}
                            label="Timezone"
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <PublicIcon
                                  sx={{
                                    ml: 1,
                                    mr: 0.5,
                                    color: "text.secondary",
                                  }}
                                  fontSize="small"
                                />
                              ),
                            }}
                          />
                        )}
                        sx={{ maxWidth: "100%" }}
                      />
                    </Box>
                  )}
                </Section>

                {/* Section 2: Device & Agent Visibility */}
                <Section>
                  <SectionTitle variant="subtitle1">
                    <MobileIcon color="primary" />
                    Device & Agent Visibility
                  </SectionTitle>

                  <SettingsToggle>
                    <ToggleInfo>
                      <ToggleLabel>Hide on Mobile</ToggleLabel>
                      <ToggleDescription>
                        Widget won't appear on mobile devices
                      </ToggleDescription>
                    </ToggleInfo>
                    <Switch
                      checked={hideOnMobile}
                      onChange={(e) => setHideOnMobile(e.target.checked)}
                      color="primary"
                    />
                  </SettingsToggle>

                  <SettingsToggle>
                    <ToggleInfo>
                      <ToggleLabel>Hide When Offline</ToggleLabel>
                      <ToggleDescription>
                        Hide widget when no agents are online
                      </ToggleDescription>
                    </ToggleInfo>
                    <Switch
                      checked={hideWhenOffline}
                      onChange={(e) => setHideWhenOffline(e.target.checked)}
                      color="primary"
                    />
                  </SettingsToggle>
                </Section>

                {/* Section 3: Display Timing */}
                <Section sx={{ mb: 0 }}>
                  <SectionTitle variant="subtitle1">
                    <TimerIcon color="primary" />
                    Display Timing
                  </SectionTitle>

                  <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                    <StyledTextField
                      label="Dialog Display Delay"
                      type="number"
                      value={dialogDelaySeconds}
                      onChange={(e) => setDialogDelaySeconds(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      inputProps={{ min: 0, max: 300 }}
                      helperText="Seconds after page load before widget appears (0-300)"
                      InputProps={{
                        endAdornment: (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mr: 1 }}
                          >
                            sec
                          </Typography>
                        ),
                      }}
                    />
                  </Box>
                </Section>
              </ConfigPanel>
            </Grid>

            {/* Preview Panel */}
            <Grid item xs={12} md={7}>
              <PreviewContainer elevation={2}>
                <PreviewLabel>
                  <VisibilityIcon
                    sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }}
                  />
                  Live Preview
                </PreviewLabel>

                <WebsitePreview>
                  <BrowserBar>
                    <BrowserDot color="#ff5f57" />
                    <BrowserDot color="#febc2e" />
                    <BrowserDot color="#28c840" />
                    <UrlBar>
                      {widgetData?.business_details?.website_url ||
                        "https://yourwebsite.com"}
                    </UrlBar>
                  </BrowserBar>

                  <WebsiteContent>
                    {/* Placeholder content to simulate website */}
                    <ContentPlaceholder width="60%" />
                    <ContentPlaceholder width="90%" />
                    <ContentPlaceholder width="75%" />
                    <Box sx={{ my: 2 }} />
                    <ContentPlaceholder width="100%" />
                    <ContentPlaceholder width="85%" />
                    <ContentPlaceholder width="45%" />
                    <Box sx={{ my: 2 }} />
                    <ContentPlaceholder width="70%" />
                    <ContentPlaceholder width="80%" />

                    {/* Delay indicator */}
                    {dialogDelaySeconds > 0 && (
                      <DelayIndicator>
                        <TimerIcon sx={{ fontSize: 12 }} />
                        {dialogDelaySeconds}s delay
                      </DelayIndicator>
                    )}

                    {/* Floating widget button */}
                    <FloatingButtonPreview
                      buttoncolor={buttonColor}
                      isvisible={isWidgetVisible.toString()}
                    >
                      <ChatIcon sx={{ color: fontColor, fontSize: 28 }} />
                    </FloatingButtonPreview>
                  </WebsiteContent>
                </WebsitePreview>

                {/* Status Summary Card */}
                <StatusCard>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ mb: 1.5 }}
                  >
                    Current Configuration
                  </Typography>

                  <StatusRow>
                    <StatusLabel>Availability</StatusLabel>
                    <StatusBadge status={alwaysOn ? "active" : "inactive"}>
                      {alwaysOn ? (
                        <>
                          <CheckCircleIcon sx={{ fontSize: 14 }} />
                          Always On
                        </>
                      ) : customScheduleEnabled ? (
                        <>
                          <ScheduleIcon sx={{ fontSize: 14 }} />
                          Custom Schedule
                        </>
                      ) : (
                        <>
                          <CancelIcon sx={{ fontSize: 14 }} />
                          Off
                        </>
                      )}
                    </StatusBadge>
                  </StatusRow>

                  {customScheduleEnabled && (
                    <StatusRow>
                      <StatusLabel>Timezone</StatusLabel>
                      <Typography variant="body2" color="text.secondary">
                        {timezone}
                      </Typography>
                    </StatusRow>
                  )}

                  <StatusRow>
                    <StatusLabel>Mobile Devices</StatusLabel>
                    <StatusBadge status={hideOnMobile ? "inactive" : "active"}>
                      {hideOnMobile ? (
                        <>
                          <VisibilityOffIcon sx={{ fontSize: 14 }} />
                          Hidden
                        </>
                      ) : (
                        <>
                          <VisibilityIcon sx={{ fontSize: 14 }} />
                          Visible
                        </>
                      )}
                    </StatusBadge>
                  </StatusRow>

                  <StatusRow>
                    <StatusLabel>When Offline</StatusLabel>
                    <StatusBadge
                      status={hideWhenOffline ? "inactive" : "active"}
                    >
                      {hideWhenOffline ? (
                        <>
                          <VisibilityOffIcon sx={{ fontSize: 14 }} />
                          Hidden
                        </>
                      ) : (
                        <>
                          <VisibilityIcon sx={{ fontSize: 14 }} />
                          Visible
                        </>
                      )}
                    </StatusBadge>
                  </StatusRow>

                  <StatusRow>
                    <StatusLabel>Display Delay</StatusLabel>
                    <Typography variant="body2" color="text.secondary">
                      {dialogDelaySeconds} second
                      {dialogDelaySeconds !== 1 ? "s" : ""}
                    </Typography>
                  </StatusRow>
                </StatusCard>
              </PreviewContainer>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default VisibilitySettings;
